import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { ConfigProvider } from "antd";
import { Amplify } from "aws-amplify";
import { Auth } from "aws-amplify";

import Dashboard from "./components/Dashboard";
import AssessmentForm from "./components/AssessmentForm";
import UserManagement from "./components/UserManagement";
import AssessmentList from "./components/AssessmentList";
import AssessmentDetail from "./components/AssessmentDetail";
import Profile from "./components/Profile";
import Settings from "./components/Settings";
import Signup from "./components/SignUp";
import VerifyEmail from "./components/VerifyEmail";
import SignIn from "./components/SignIn";
import Layout from "./components/Layout";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Configure Amplify
const amplifyConfig = {
  Auth: {
    region: process.env.REACT_APP_AWS_REGION || "us-east-1",
    userPoolId: process.env.REACT_APP_USER_POOL_ID || "",
    userPoolWebClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID || "",
    identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID || "",
  },
  API: {
    endpoints: [
      {
        name: "cmda-api",
        endpoint: process.env.REACT_APP_API_URL || "https://api.example.com",
        region: process.env.REACT_APP_AWS_REGION || "us-east-1",
      },
    ],
  },
};

Amplify.configure(amplifyConfig);

// Component to handle /aws/activate redirect
const ActivateRedirect: React.FC = () => {
  return <Navigate to="/signup?from=activation" replace />;
};

/** Inner app that uses auth context */
const AuthenticatedApp: React.FC = () => {
  // Single source of truth: AuthContext already tracks session state and
  // listens to Amplify Hub events. Re-calling Auth.currentAuthenticatedUser
  // here (keyed on `user`) created an API-call feedback loop to Cognito.
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return null;
  }

  if (!user) {
    return (
      <SignIn onSignIn={() => navigate("/dashboard", { replace: true })} />
    );
  }

  const handleSignOut = async () => {
    await Auth.signOut();
  };

  return (
    <Layout user={user} signOut={handleSignOut}>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/assessments" element={<AssessmentList />} />
        <Route path="/assessments/new" element={<AssessmentForm />} />
        <Route
          path="/assessments/:assessmentId"
          element={<AssessmentDetail />}
        />
        <Route
          path="/assessments/:assessmentId/edit"
          element={<AssessmentForm />}
        />
        <Route path="/users" element={<UserManagement />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#1890ff",
          borderRadius: 6,
        },
      }}
    >
      <AuthProvider>
        <Router>
          <Routes>
            {/* AWS Marketplace activation redirect */}
            <Route path="/aws/activate" element={<ActivateRedirect />} />

            {/* Signup route - accessible without authentication */}
            <Route path="/signup" element={<Signup />} />

            {/* Email verification route - accessible without authentication */}
            <Route path="/verify-email" element={<VerifyEmail />} />

            {/* All other routes use custom auth */}
            <Route path="*" element={<AuthenticatedApp />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ConfigProvider>
  );
};

export default App;
